/**
 * Duplicate a declaration into the package it is declared in.
 *
 * POST /api/{plural} takes a package, an ID and the kind's own fields, and
 * duplicating is that with the fields read off an existing row first. Adding
 * from nothing used to be this dialog too; since issue #53 it is the tree's
 * New dialog, which hands each kind to its own wizard instead of a generic form.
 *
 * The destination is passed in already resolved from containment, never
 * assembled from a literal, because a package's own qualified name is the only
 * spelling guaranteed to resolve.
 *
 * A duplicate copies the fields the registry declares and nothing else. Derived
 * fields are skipped: the write path refuses them, so sending one would turn a
 * duplicate into a validation error about a field nobody typed.
 */

import { useEffect, useState } from 'react'
import Alert from '@mui/material/Alert'
import Autocomplete from '@mui/material/Autocomplete'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { ApiError, api } from '../../api'
import type { ElementRow, EntityRow, FieldSchema, KindSchema, Schema } from '../../schema'
import { MONOSPACE } from '../../theme'

/**
 * Whether a field may be sent on a create.
 *
 * `derived` is read from the schema rather than guessed from the field NAME,
 * which is the hard-coded field list the whole registry exists to avoid. The
 * write path REFUSES a derived field rather than ignoring it, so sending one
 * fails the request and takes the fields beside it down with it.
 */
function isWritable(field: FieldSchema): boolean {
  if (field.derived) return false
  return !['name', 'identifier', 'type'].includes(field.kind)
}

/** A value worth copying: something create can carry, and not empty. */
function isCopyable(value: unknown): boolean {
  if (typeof value === 'string') return value !== ''
  if (typeof value === 'boolean' || typeof value === 'number') return true
  return Array.isArray(value) && value.length > 0 && value.every((item) => typeof item === 'string')
}

export default function AddElementDialog({
  schema,
  pkg,
  source,
  onClose,
  onCreated,
}: {
  schema: Schema
  /** Qualified name of the package the declaration goes in. */
  pkg: string
  /** The row being duplicated. */
  source: ElementRow
  onClose: () => void
  onCreated: (message: string) => void
}) {
  const sourceKind: KindSchema | undefined = schema.kinds.find(
    (entry) => entry.name === source.kind,
  )
  const kind = sourceKind

  const [identifier, setIdentifier] = useState('')
  const [name, setName] = useState(`${source.name || source.id} copy`)
  const [typeName, setTypeName] = useState(source.type_name ?? '')
  const [keyword, setKeyword] = useState('')
  const [copied, setCopied] = useState<Record<string, unknown> | null>(null)
  const [copyNote, setCopyNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState('')
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  // Read the row being duplicated once, in detail: a listing drops list and
  // reference fields, so copying from the tree row alone would quietly lose the
  // statement, which is most of what a requirement IS.
  useEffect(() => {
    if (!sourceKind || !source.id) return
    let live = true
    api
      .get(sourceKind.plural, source.id)
      .then((row: EntityRow) => {
        if (!live) return
        const fields: Record<string, unknown> = {}
        for (const field of sourceKind.fields) {
          if (!isWritable(field)) continue
          const value = row[field.name]
          if (isCopyable(value)) fields[field.name] = value
        }
        setCopied(fields)
        setCopyNote(
          Object.keys(fields).length
            ? `Copying ${Object.keys(fields).length} stated field(s) from ${source.id}.`
            : `${source.id} states no fields to copy.`,
        )
      })
      .catch((error: ApiError) => {
        if (!live) return
        setCopied({})
        setCopyNote(
          `Only the type is being copied: reading ${source.id} back failed (${error.message}).`,
        )
      })
    return () => {
      live = false
    }
  }, [source, sourceKind])

  const submit = async () => {
    setBusy(true)
    setFailure('')
    setFieldErrors({})
    try {
      if (!kind) throw new ApiError(0, `BEST does not project a ${source.kind}`)

      const body: Record<string, unknown> = { ...(copied ?? {}), package: pkg, identifier }
      if (name) body.name = name
      if (typeName) body.type_name = typeName
      if (kind.keywords.length) body.keyword = keyword

      const created = await api.create(kind.plural, body)
      onCreated(`${created.identifier} created in ${pkg}`)
    } catch (error) {
      const failed = error as ApiError
      setFieldErrors(failed.fields ?? {})
      if (!Object.keys(failed.fields ?? {}).length) setFailure(failed.message)
    } finally {
      setBusy(false)
    }
  }

  const ready =
    Boolean(identifier) && Boolean(kind) && (!kind?.keywords.length || Boolean(keyword))

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        Duplicate {source.id || source.name}
        <Typography variant="caption" color="text.secondary" component="div">
          In <span style={{ fontFamily: MONOSPACE }}>{pkg}</span>
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {failure && <Alert severity="error">{failure}</Alert>}
          {copyNote && <Alert severity="info">{copyNote}</Alert>}
          {!sourceKind && (
            <Alert severity="warning">
              BEST does not project a {source.kind}, so there is nothing to copy from it.
            </Alert>
          )}

          <TextField
            label="ID"
            required
            value={identifier}
            onChange={(event) => setIdentifier(event.target.value)}
            error={Boolean(fieldErrors.identifier)}
            helperText={fieldErrors.identifier || 'The short name, in <angle brackets>'}
            size="small"
            slotProps={{ input: { sx: { fontFamily: MONOSPACE } } }}
          />

          <TextField
            label="Name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            error={Boolean(fieldErrors.name)}
            helperText={fieldErrors.name || 'The declared name; defaults to the short name'}
            size="small"
          />

          {kind && kind.keywords.length > 0 && (
            <FormControl size="small" required>
              <InputLabel id="tree-new-keyword">Sort</InputLabel>
              <Select
                labelId="tree-new-keyword"
                label="Sort"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
              >
                {kind.keywords.map((choice) => (
                  <MenuItem key={choice.name} value={choice.name}>
                    {choice.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {kind && (
            <Autocomplete
              freeSolo
              options={kind.types.map((choice) => choice.name)}
              value={typeName}
              onChange={(_event, next) => setTypeName(next ?? '')}
              onInputChange={(_event, next) => setTypeName(next)}
              size="small"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Type"
                  required={kind.type_required}
                  error={Boolean(fieldErrors.type_name)}
                  helperText={fieldErrors.type_name || 'Any valid type; the list is a shortcut'}
                />
              )}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => void submit()} disabled={!ready || busy}>
          {busy ? 'Creating…' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
